import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { ToastService } from '../services/toast.service';

/**
 * OFP-009 — backstop for writes the offline hub queued but cannot replay.
 *
 * Any `/pos/checks` write with no explicit hub handler lands in the hub's
 * generic outbox, which persists a row and answers a synthetic
 * `202 { success: true, data: { _offline: { queued: true } } }`. For pax
 * that is correct: it replays at drain. For anything else the drain has no
 * classifier, so the row is dead-lettered and — through the per-check
 * quarantine — holds back that check's later bill and payment.
 *
 * The per-action gates (servedByHub) keep the known routes away from the
 * hub. This catches whatever still gets through. The row is already
 * persisted, so the call is NOT turned into a failure (that would invite a
 * retry and a second row); the cashier is told plainly instead, and pointed
 * at Sync Issues.
 */
const REPLAYABLE_WHEN_QUEUED: readonly RegExp[] = [
  /\/pos\/checks\/[^/]+\/pax$/,
];

/** True when a reply is the hub's queued-write shape for a route that cannot replay. */
export function isUnreplayableHubQueue(url: string, body: unknown): boolean {
  const offline = (body as { data?: { _offline?: { queued?: unknown } } } | null)?.data?._offline;
  if (offline?.queued !== true) return false;
  const path = url.split('?')[0];
  return !REPLAYABLE_WHEN_QUEUED.some(r => r.test(path));
}

export const hubQueuedWarningInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  return next(req).pipe(
    tap(evt => {
      if (evt instanceof HttpResponse && isUnreplayableHubQueue(req.url, evt.body)) {
        console.warn(`[OFP-009] ${req.method} ${req.url} was queued on the local hub, which cannot replay it.`);
        toast.info('pos.offline.queuedOnHub', 12000);
      }
    }),
  );
};
